import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import { actionCreators, ICityViewState } from '../modules/cityView'
import CityDemographics from './CityDemographics'
import CityDetails from './CityDetails'
import CityFiles from './CityFiles'

export type CityViewPageProps = ICityViewState &
  typeof actionCreators &
  RouteComponentProps<{ citySlug: string }>

export class CityViewPage extends React.PureComponent<CityViewPageProps> {
  constructor(props: CityViewPageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadCity(this.props.match.params.citySlug)
  }

  public componentDidUpdate(prevProps: CityViewPageProps) {
    if (prevProps.match.params.citySlug !== this.props.match.params.citySlug) {
      this.props.loadCity(this.props.match.params.citySlug)
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (this.props.isLoading) {
      return (
        <DocumentTitle title='City Profile :: Loading...'>
          <div className='my-3 text-center'>
            Retrieving city profile information. Please wait...
          </div>
        </DocumentTitle>
      )
    } else if (!this.props.city.id) {
      return (
        <DocumentTitle title='City Profile :: Error'>
          <div className='my-3 text-center'>Unable to load city profile</div>
        </DocumentTitle>
      )
    }

    return (
      <DocumentTitle title={`${this.props.city.place.cityState} :: Profile`}>
        <Container className='my-3'>
          {this._renderHeader()}
          {this._renderTabs()}
          <Switch>
            <Route
              path={`${this.props.match.path}/demographics`}
              render={() => <CityDemographics {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/files`}
              render={() => <CityFiles {...this.props} />}
            />
            <Route render={() => <CityDetails {...this.props} />} />
          </Switch>
        </Container>
      </DocumentTitle>
    )
  }

  private _onEdit() {
    this.props.history.push(
      `/development/city/${this.props.match.params.citySlug}/edit`,
    )
  }

  private _onUpdateDemographics() {
    this.props.updateDemographics(this.props.city.id)
  }

  private _renderHeader() {
    return (
      <Card>
        <CardHeader className='bg-secondary text-light d-flex justify-content-between'>
          <div>
            <FontAwesomeIcon icon={['fal', 'industry-alt']} fixedWidth /> City
            Profile
          </div>
          <div>
            {this.props.permissions.canEdit === true && (
              <UncontrolledDropdown className='d-line-block'>
                <DropdownToggle tag='a' className='px-1 text-center'>
                  <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={this._onEdit}>Edit</DropdownItem>
                  <DropdownItem
                    disabled={this.props.isUpdatingDemographics}
                    onClick={this._onUpdateDemographics}
                  >
                    Update Demographics
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <div className='display-4'>{this.props.city.place.cityState}</div>
        </CardBody>
      </Card>
    )
  }

  private _renderTabs() {
    return (
      <NavTabs className='my-3'>
        <NavTabItem
          exact
          iconName='list'
          label='Details'
          to={this.props.match.url}
        />
        <NavTabItem
          iconName='chart-bar'
          label='Demographics'
          to={`${this.props.match.url}/demographics`}
        />
        <NavTabItem
          badgeCount={
            this.props.city.attachments.size + this.props.city.photos.size
          }
          iconName='paperclip'
          label='Files'
          to={`${this.props.match.url}/files`}
        />
      </NavTabs>
    )
  }
}
